// Word-level differences inside a changed paragraph (design §13).
//
// The server has already said which paragraphs changed; this is the last mile,
// so a reader can see the sentence that moved rather than two blocks of prose
// to compare by eye. A longest-common-subsequence over words, which is small
// and exact, and there is never much text in one paragraph.

export type Piece = { kind: "same" | "added" | "removed"; text: string };

/** Splits into words and the whitespace between them, so rejoining the pieces
 *  reproduces the text exactly. */
function tokens(text: string): string[] {
  return text.match(/\s+|[^\s]+/g) ?? [];
}

/** Above this many tokens on either side the matrix stops being small. A
 *  paragraph never gets there by being written; it gets there by having a
 *  document pasted into it, and that is not a case worth freezing a tab for. */
const TOO_LONG = 3000;

export function wordDiff(before: string, after: string): Piece[] {
  const a = tokens(before);
  const b = tokens(after);
  if (a.length > TOO_LONG || b.length > TOO_LONG) {
    return [
      { kind: "removed", text: before },
      { kind: "added", text: after },
    ];
  }

  // lcs[i][j] = length of the longest common run of a[i:] and b[j:].
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  const out: Piece[] = [];
  const push = (kind: Piece["kind"], text: string) => {
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += text;
    else out.push({ kind, text });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push("same", a[i]!);
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      push("removed", a[i]!);
      i++;
    } else {
      push("added", b[j]!);
      j++;
    }
  }
  while (i < a.length) push("removed", a[i++]!);
  while (j < b.length) push("added", b[j++]!);
  return out;
}

/** The text as it reads on one side of the change. Dropping the other side's
 *  pieces must give back the original exactly, which is the property the
 *  renderer relies on and the tests pin: a piece is never re-labelled to tidy
 *  up the highlighting, because a space marked unchanged would then appear on
 *  both sides and neither would reconstruct. */
export function side(pieces: Piece[], which: "before" | "after"): string {
  const drop = which === "before" ? "added" : "removed";
  return pieces
    .filter((p) => p.kind !== drop)
    .map((p) => p.text)
    .join("");
}
